import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useFetch, Panel, Stat, Tag, dirTone, pnlClass, Empty } from "@/components/Common";
import { api, errMsg, fmtMoney, fmtPrice, fmtTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { HealthPanel, BotsPanel, RejectedPanel, EventsPanel } from "@/components/SystemPanels";
import { Button } from "@/components/ui/button";
import PremiumOverview from "@/components/PremiumOverview";
import RealObserverStatus from "@/components/RealObserverStatus";

function Quotes({ direct }) {
  const { data } = useFetch("/markets/quotes", [], direct ? 1000 : 15000);
  const [clock, setClock] = useState(Date.now());
  useEffect(() => {
    if (!direct) return;
    const timer = setInterval(() => setClock(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [direct]);
  const visibleQuotes = (data || Array.from({ length: 7 })).map(q => {
    if (q?.source !== 'cloud_mt5_direct' || q.error) return q;
    const receipt = Date.parse(q.as_of);
    // Client-side expiry also applies when the API itself is unreachable and
    // useFetch retains an old successful response. Never display it as live.
    if (!Number.isFinite(receipt) || clock - receipt > 5000 || receipt - clock > 5000)
      return { ...q, price: null, error: 'DIRECT_READER_STALE' };
    return q;
  });
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-7" data-testid="quotes-strip">
      {visibleQuotes.slice(0, 13).map((q, i) => (
        <div key={q?.symbol || i} className="rounded border bg-card/70 px-3 py-2" data-testid={q ? `quote-${q.symbol}` : undefined}>
          <div className="flex justify-between"><span className="font-mono text-xs">{q?.symbol || "···"}</span>{q?.change_pct != null && !q.error && <span className={`num text-xs ${pnlClass(q.change_pct)}`}>{q.change_pct > 0 ? "+" : ""}{q.change_pct}%</span>}</div>
          <div className="num mt-1 text-sm font-semibold">{q?.price ? fmtPrice(q.price, q.symbol) : q?.error ? "n/a" : "—"}</div>
          {q && <div className="mt-1 text-[10px] text-muted-foreground" title={q.error || q.as_of}>{q.error ? (q.source === 'cloud_mt5_direct' ? 'Cloud reader disconnected / stale' : 'Broker data unavailable') : `${q.source === 'cloud_mt5_direct' ? 'Cloud MT5 · receipt' : q.source} · ${fmtTime(q.as_of)}`}</div>}
          {q?.source === 'cloud_mt5_direct' && !q.error && <div className="mt-1 font-mono text-[10px] text-muted-foreground">Bid {fmtPrice(q.bid, q.symbol)} / Ask {fmtPrice(q.ask, q.symbol)} · tick {q.tick_idle_seconds == null ? 'not observed' : `${q.tick_idle_seconds.toFixed(1)}s ago`}</div>}
        </div>
      ))}
    </div>
  );
}

function EngineCard({ engine, reload }) {
  const { can } = useAuth();
  const cfg = engine.config, st = engine.state || {};
  const toggle = async (v) => {
    try { await api.put("/engine", { enabled: v }); toast.success(v ? "Engine enabled — scanning will continue server-side" : "Engine paused"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const scan = async () => {
    try { await api.post("/engine/scan"); toast.success("Scan started on server"); setTimeout(reload, 4000); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Automated engine" testid="engine-card" right={<Switch checked={cfg.enabled} disabled={!can("manage_engine") || (!cfg.enabled && engine.direct_bridge && !engine.direct_bridge.ready)} onCheckedChange={toggle} data-testid="engine-enabled-switch" />}>
      {engine.direct_bridge && !engine.direct_bridge.ready && <p className="mb-3 text-xs text-[hsl(var(--warn))]">{engine.direct_bridge.block}. Dashboard prices are connected separately from execution.</p>}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div><div className="label">Destination</div><div className="mt-1 font-mono" data-testid="engine-destination">{cfg.destination}</div></div>
        <div><div className="label">Interval</div><div className="mt-1 font-mono">{cfg.interval_minutes} min</div></div>
        <div><div className="label">Last scan</div><div className="mt-1 font-mono text-xs" data-testid="engine-last-scan">{fmtTime(st.last_scan_at)}</div></div>
        <div><div className="label">Next scan</div><div className="mt-1 font-mono text-xs">{cfg.enabled ? fmtTime(st.next_scan_at) : "paused"}</div></div>
        <div><div className="label">Candidates</div><div className="mt-1 font-mono">{st.last_candidates ?? "—"}</div></div>
        <div><div className="label">Executed</div><div className="mt-1 font-mono">{st.last_executed ?? "—"}</div></div>
      </div>
      {st.running && <div className="mt-3"><Tag tone="blue">scanning…</Tag></div>}
      {st.last_error && <p className="mt-3 text-xs text-[hsl(var(--warn))]">{st.last_error}</p>}
      {can("manage_engine") && <Button variant="outline" className="mt-4 w-full" disabled={engine.direct_bridge && !engine.direct_bridge.ready} onClick={scan} data-testid="engine-scan-now-button">Scan watchlist now</Button>}
    </Panel>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { data, reload, error, updatedAt } = useFetch("/dashboard", [], 5000);
  const { data: an } = useFetch(`/analytics?destination=${data?.destination || "paper"}`, [], 20000);
  if (!data) return error ? <div role="alert" className="rounded-xl border p-6">Panel verisi alınamadı. <Button variant="outline" onClick={reload}>Tekrar dene</Button></div> : <div aria-label="Panel yükleniyor" className="h-96 animate-pulse rounded bg-card" />;
  const m = data.master?.report;
  const broker = data.destination !== "paper";
  const equity = broker ? data.account?.equity : data.paper_equity;
  const money = (v) => v == null ? "—" : broker && data.account?.currency ? new Intl.NumberFormat("en-GB", { style: "currency", currency: data.account.currency }).format(v) : fmtMoney(v);
  const oldReview = data.master && Date.now() - new Date(data.master.created_at).getTime() > 15 * 60000;
  return (
    <div className="space-y-6">
      <PremiumOverview dashboard={data} money={money} updatedAt={updatedAt} error={error} />
      {user?.role === 'owner' && <RealObserverStatus/>}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground"><span>Data provider: {data.data_provider === 'cloud_mt5_direct' ? 'Cloud MT5 · direct terminal feed' : data.data_provider || 'Unavailable'}</span><span>Fiyat akışı ve emir hazırlığı ayrı kontrol edilir.</span></div>
      <Quotes direct={data.data_provider === 'cloud_mt5_direct'} />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label={broker ? "Broker equity" : "Paper equity"} value={money(equity)} sub={broker ? data.account?.telemetry_missing ? 'Reader account telemetry not yet supplied' : data.account?.error ? "Account read failed" : `${data.account?.name || "Selected account"} · ${data.destination}` : undefined} testid="stat-paper-equity" />
        <Stat label={broker ? "Broker floating P/L" : "Today P/L"} value={broker ? money(data.account?.profit) : fmtMoney(data.today_pnl)} tone={pnlClass(broker ? data.account?.profit : data.today_pnl)} sub={`${data.today_trades} tracked closures today`} testid="stat-today-pnl" />
        <Stat label="Open positions" value={data.open_trades.length} testid="stat-open-positions" />
        <Stat label={`Tracked win rate (${data.destination})`} value={an ? `${an.summary.win_rate}%` : "—"} sub={an ? `${an.summary.trades} tracked trades · PF ${an.summary.profit_factor ?? "—"}` : ""} testid="stat-win-rate" />
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        <HealthPanel />
        <BotsPanel engine={data.engine} analytics={an} />
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        <RejectedPanel items={data.rejected} />
        <EventsPanel />
      </div>
      <div className="grid gap-4 xl:grid-cols-[2fr_1fr]">
        <Panel title={`Tracked equity curve (${data.destination})`}>
          {an?.equity_curve?.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={an.equity_curve}>
                <defs><linearGradient id="eq" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00d6a8" stopOpacity={0.35} /><stop offset="100%" stopColor="#00d6a8" stopOpacity={0} /></linearGradient></defs>
                <XAxis dataKey="time" hide /><YAxis domain={["auto", "auto"]} tick={{ fill: "#94a3b8", fontSize: 11 }} width={70} />
                <Tooltip contentStyle={{ background: "#121826", border: "1px solid #2a3142" }} />
                <Area type="monotone" dataKey="equity" stroke="#00d6a8" fill="url(#eq)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <Empty>No closed trades yet. The engine only trades validated opportunities — it never forces trades.</Empty>}
        </Panel>
        <EngineCard engine={data.engine} reload={reload} />
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        <Panel title="Open positions" right={<Link to="/trades" className="text-xs text-primary hover:underline">All trades</Link>}>
          {data.open_trades.length ? data.open_trades.map((t) => (
            <div key={t.id} className="flex items-center justify-between border-b border-border/50 py-2 text-sm" data-testid={`open-position-${t.id}`}>
              <div className="flex items-center gap-2"><Tag tone={dirTone(t.direction)}>{t.direction}</Tag><span className="font-mono">{t.symbol}</span><Tag>{t.destination}</Tag></div>
              <div className="num text-xs text-muted-foreground">@ {fmtPrice(t.entry, t.symbol)} · SL {fmtPrice(t.stop_loss, t.symbol)} · TP {fmtPrice(t.take_profit, t.symbol)}</div>
            </div>
          )) : <Empty>No open positions</Empty>}
        </Panel>
        <Panel title="Portfolio Control" testid="master-ai-card" right={data.master && <span className="font-mono text-xs text-muted-foreground">{fmtTime(data.master.created_at)}</span>}>
          {m && !oldReview ? (
            <div className="space-y-2 text-sm">
              <div className="flex gap-2"><Tag tone="gold">{m.market_regime || "regime n/a"}</Tag><Tag tone="blue">{m.overall_bias || "—"}</Tag></div>
              <p className="text-muted-foreground">{m.summary}</p>
              {m.approved?.length > 0 && <div className="text-xs">Approved: <span className="font-mono text-up">{m.approved.join(", ")}</span></div>}
            </div>
          ) : <Empty>{oldReview ? "Previous portfolio review is historical, not a current market decision. A new review runs when qualified candidates exist." : "A portfolio review runs when qualified candidates exist."}</Empty>}
        </Panel>
      </div>
      <Panel title="Latest AI decisions" right={<Link to="/trades?tab=journal" className="text-xs text-primary hover:underline">Journal</Link>}>
        {data.recent_journal.length ? data.recent_journal.map((j) => (
          <div key={j.id} className="grid grid-cols-[90px_70px_1fr_90px] items-center gap-3 border-b border-border/50 py-2 text-sm">
            <span className="font-mono">{j.symbol}</span><Tag tone={dirTone(j.direction)}>{j.direction || "—"}</Tag>
            <span className="truncate text-muted-foreground">{j.strategy || "no setup"} · {j.reasons?.join(", ") || j.ai?.reasoning}</span><Tag tone={j.action === "executed" ? "up" : "muted"}>{j.action}</Tag>
          </div>
        )) : <Empty>No decisions yet</Empty>}
      </Panel>
    </div>
  );
}
